import React, { createContext, JSX, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth0, User as Auth0User } from '@auth0/auth0-react'
import { graphqlRequest } from 'lib/helpers'
import { ProfilePictureSyncResult } from 'types/api'
import syncMyProfilePictureMutation from 'graphql/syncMyProfilePicture.graphql'
import logger from 'logger'

const log = logger.extend('context')

let pictureSyncFired = false

const isGooglePictureUrl = (url: string): boolean => {
  try {
    return new URL(url).hostname.endsWith('.googleusercontent.com')
  } catch {
    return false
  }
}

type UserRole = 'superuser' | 'admin' | 'streamer'
export interface ExtendedUser extends Auth0User {
  superuser?: boolean
  admin: boolean
  eventIds?: string[]
}

export interface StreamToolsUser {
  userId?: string
  name?: string
  email?: string
  picture?: string
  role?: UserRole
  superuser: boolean
  admin: boolean
}

export interface AuthContextType {
  isAuthenticated: boolean
  streamToolsUser: StreamToolsUser | null
  auth0Token: string | undefined
  updateProfilePicture: (url: string) => void
}

const defaultUser: StreamToolsUser = {
  name: '',
  email: '',
  role: undefined,
  superuser: false,
  admin: false
}

const defaultContext: AuthContextType = {
  isAuthenticated: false,
  streamToolsUser: defaultUser,
  auth0Token: undefined,
  updateProfilePicture: () => {}
}

export const AuthContext = createContext<AuthContextType>(defaultContext)
AuthContext.displayName = 'Auth Context'

export const UserProvider = ({ children }): JSX.Element => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0()

  const [streamToolsUser, setStreamToolsUser] = useState<StreamToolsUser | null>(null)
  const [auth0Token, setAuth0Token] = useState<string>()

  log({ func: 'UserProvider', children })

  useEffect(() => {
    if (!isAuthenticated || user == null) return

    const initializeUser = async (): Promise<void> => {
      const token = await getAccessTokenSilently()
      setAuth0Token(token)
      const extendedUser = user as ExtendedUser
      const roleMap = [
        { condition: extendedUser.superuser, role: 'superuser' as UserRole },
        { condition: extendedUser.admin, role: 'admin' as UserRole },
        { condition: extendedUser.streamer, role: 'streamer' as UserRole }
      ]
      const role = roleMap.find(r => r.condition)?.role

      const newUser: StreamToolsUser = {
        userId: extendedUser.sub,
        email: extendedUser.email,
        name: extendedUser.name,
        picture: extendedUser.picture,
        role,
        superuser: extendedUser.superuser === true,
        admin: extendedUser.admin
      }

      setStreamToolsUser(newUser)
    }

    void initializeUser()
  }, [isAuthenticated, user])

  const updateProfilePicture = useCallback((url: string): void => {
    setStreamToolsUser((prev) => prev === null ? prev : { ...prev, picture: url })
  }, [])

  useEffect(() => {
    if (pictureSyncFired) return
    if (!isAuthenticated || auth0Token === undefined) return
    const picture = streamToolsUser?.picture
    if (picture === undefined || !isGooglePictureUrl(picture)) return

    pictureSyncFired = true
    graphqlRequest<{ sync: ProfilePictureSyncResult }>(
      syncMyProfilePictureMutation,
      {},
      auth0Token
    ).then(({ sync }) => {
      if (sync?.pictureUrl != null) updateProfilePicture(sync.pictureUrl)
    }).catch((err) => {
      log({ func: 'syncMyProfilePicture', err })
    })
  }, [isAuthenticated, auth0Token, streamToolsUser, updateProfilePicture])

  return (
    <AuthContext.Provider
      value={useMemo(() => ({
        isAuthenticated,
        streamToolsUser,
        auth0Token,
        updateProfilePicture
      }), [isAuthenticated, streamToolsUser, auth0Token, updateProfilePicture])}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => useContext(AuthContext)
